import { LogLevel } from '@environment/enums/log-level.enum';
import { captureSentryMessage } from '@shared/sentry';
import { ONE_MINUTE_MS } from '@shared/utils/constants';
import { BehaviorSubject } from 'rxjs';
import { Services } from './services.service';

export enum StateBadge {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  NETWORK_DISCONNECTED = 'NETWORK_DISCONNECTED',
  BACKEND_DISCONNECTED = 'BACKEND_DISCONNECTED',
}

const NO_HEARTBEAT_THRESHOLD = 3 * ONE_MINUTE_MS;

export class ServiceWorkerStatusService {
  private readonly currentState$ = new BehaviorSubject<StateBadge>(StateBadge.INACTIVE);
  private firstHeartbeatReceived = false;
  private lastHeartbeatTime: number | null = null;

  private readonly badgeColors = {
    [StateBadge.ACTIVE]: '#B08FE6', // Purple
    [StateBadge.INACTIVE]: '#DEFF00', // Yellow
    [StateBadge.NETWORK_DISCONNECTED]: '#F44336', // Red
    [StateBadge.BACKEND_DISCONNECTED]: '#FF9800', // Orange
  };

  private readonly statusMessages = {
    [StateBadge.ACTIVE]: 'Active',
    [StateBadge.INACTIVE]: 'Inactive',
    [StateBadge.NETWORK_DISCONNECTED]: 'Network Disconnected',
    [StateBadge.BACKEND_DISCONNECTED]: 'Backend Disconnected',
  };

  constructor(private services: Services) {
    // Subscribe to state changes to update badge
    this.currentState$.subscribe(state => this.updateBadge(state));
    this.initializeStateMonitoring();
  }

  private initializeStateMonitoring(): void {
    // Network
    this.services.networkService.isOnline$.subscribe(isOnline => {
      if (!isOnline) {
        this.updateState(StateBadge.NETWORK_DISCONNECTED);
      } else {
        this.checkHeartbeatStatus();
      }
    });

    // Backend
    this.services.networkService.isBackendConnected$.subscribe(isConnected => {
      if (!isConnected && this.services.networkService.isOnline$.value) {
        this.updateState(StateBadge.BACKEND_DISCONNECTED);
      } else if (isConnected && this.currentState$.value === StateBadge.BACKEND_DISCONNECTED) {
        this.checkHeartbeatStatus();
      }
    });

    // Heartbeat
    setInterval(() => this.checkHeartbeatStatus(), ONE_MINUTE_MS);
  }

  private async checkHeartbeatStatus(): Promise<void> {
    try {
      const heartbeats = await this.services.heartbeatService.getHeartbeats();

      if (heartbeats.length > 0) {
        const lastHeartbeat = new Date(heartbeats[0]).getTime();
        this.lastHeartbeatTime = lastHeartbeat;
        this.firstHeartbeatReceived = true;

        const now = Date.now();
        const timeSinceLastHeartbeat = now - lastHeartbeat;

        if (timeSinceLastHeartbeat > NO_HEARTBEAT_THRESHOLD) {
          this.updateState(StateBadge.INACTIVE);
        } else if (this.services.networkService.isOnline$.value) {
          this.updateState(StateBadge.ACTIVE);
        }
      } else if (this.firstHeartbeatReceived) {
        this.updateState(StateBadge.INACTIVE);
      }
    } catch (error) {
      console.error('Error checking heartbeat status:', error);
      this.updateState(StateBadge.NETWORK_DISCONNECTED);
    }
  }

  private updateState(newState: StateBadge): void {
    if (this.currentState$.value !== newState) {
      captureSentryMessage(
        `Service Worker status changed to ${newState}`,
        {
          oldState: this.currentState$.value,
          newState,
        },
        LogLevel.Info
      );
      this.currentState$.next(newState);
    }
  }

  private updateBadge(state: StateBadge): void {
    chrome.action.setBadgeBackgroundColor({ color: this.badgeColors[state] });
    chrome.action.setBadgeText({ text: ' ' });
    chrome.action.setTitle({ title: this.statusMessages[state] });
  }
}
